import { useEffect, useState } from 'react'
import { useGetQuery } from 'hooks'
import { Box } from '../box'
import { getPanelConfigSchema, resolveConfig } from '../panel-config'
import { usePanelLocation } from '../use-panel-location'
import {
  chipTextColor,
  formatDistance,
  formatMinutes,
  positiveNumber,
  TRAIN_COLORS
} from './format'
import { Marquee } from './marquee'
import {
  Arrival,
  Arrivals,
  Container,
  Destination,
  Distance,
  Dot,
  Dots,
  Empty,
  Header,
  Minutes,
  Mode,
  Route,
  StopName
} from './styles'

const REFRESH_MS = 30 * 1000

const buildUrl = ({ latitude, longitude }, config) => {
  if (latitude == null) return null
  // ~110m precision keeps the API cache warm across small GPS jitter
  const params = new URLSearchParams({
    miles: String(positiveNumber(config.radius, 0.25)),
    trains: String(!!config.showTrains),
    buses: String(!!config.showBuses)
  })
  return `/transit/chicago/${latitude.toFixed(3)}/${longitude.toFixed(3)}?${params}`
}

export const Transit = ({ details }) => {
  const config = resolveConfig(getPanelConfigSchema('transit'), details?.config)
  const geo = usePanelLocation(config)
  const url = buildUrl(geo, config)
  const pageMs = positiveNumber(config.pageSeconds, 8) * 1000

  const { data, reFetch, setEnabled, setPath } = useGetQuery({
    enabled: !!url,
    url: url || '/transit/chicago/0/0'
  })
  const [page, setPage] = useState(0)

  useEffect(() => {
    if (!url) return
    setPath(url)
    setEnabled(true)
  }, [url])

  useEffect(() => {
    const id = setInterval(() => reFetch(true), REFRESH_MS)
    return () => clearInterval(id)
  }, [])

  const stops = data?.stops || []

  // Page through nearby stops
  useEffect(() => {
    if (stops.length <= 1) return
    const id = setInterval(() => setPage((p) => (p + 1) % stops.length), pageMs)
    return () => clearInterval(id)
  }, [stops.length, pageMs])

  useEffect(() => {
    if (page >= stops.length) setPage(0)
  }, [stops.length])

  if (!url || data === undefined) return <Box />

  const errors = data?.errors || {}
  const enabledModes = [config.showTrains && 'trains', config.showBuses && 'buses'].filter(Boolean)
  const notConfigured =
    enabledModes.length > 0 && enabledModes.every((m) => errors[m] === 'not_configured')

  if (stops.length === 0) {
    return (
      <Box>
        <Empty>
          {notConfigured
            ? 'Transit is not configured'
            : enabledModes.length === 0
              ? 'Enable trains or buses in this panel’s settings'
              : 'No nearby arrivals'}
        </Empty>
      </Box>
    )
  }

  const stop = stops[Math.min(page, stops.length - 1)]

  return (
    <Box>
      <Container>
        <Header>
          <Mode>{stop.type === 'train' ? 'L' : 'BUS'}</Mode>
          <StopName>
            <Marquee key={stop.id}>{stop.name}</Marquee>
          </StopName>
          <Distance>{formatDistance(stop.distance)}</Distance>
        </Header>
        <Arrivals>
          {stop.arrivals.map((a, i) => (
            <Arrival key={`${a.routeCode}-${a.destination}-${i}`}>
              <Route
                $bg={stop.type === 'train' ? TRAIN_COLORS[a.routeCode] : undefined}
                $fg={stop.type === 'train' ? chipTextColor(a.routeCode) : undefined}
              >
                {a.route}
              </Route>
              <Destination>{a.destination}</Destination>
              <Minutes $delayed={a.delayed}>{formatMinutes(a)}</Minutes>
            </Arrival>
          ))}
        </Arrivals>
        {stops.length > 1 && (
          <Dots>
            {stops.map((s, i) => (
              <Dot key={`${s.type}-${s.id}`} $active={i === page} />
            ))}
          </Dots>
        )}
      </Container>
    </Box>
  )
}
